/**
 * Saving a file the API only gives to an authenticated caller (P3-TR4, SEC-3).
 *
 * The reports hub used to link to `/api/v1/exports/...` with a plain anchor.
 * That cannot work here and never did: the access token lives in memory and is
 * attached by the axios request interceptor, and `authenticate` reads only an
 * `Authorization: Bearer` header — there is no cookie it could fall back to.
 * A browser following an anchor sends neither, so every export came back 401
 * and no report has ever downloaded.
 *
 * Fetching through `api` instead fixes it at the cause and brings the rest of
 * the client's behaviour with it: the single-flight refresh (D-011), so a
 * download attempted fifteen minutes into a session renews the token rather
 * than logging the partner out, and the same error envelope every other screen
 * reads.
 */

import { api } from '../api/client.js';

/** `attachment; filename="pnl-2026-09-15.pdf"` — quoted or not. */
const FILENAME = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

/**
 * The name the server asked for, or a fallback.
 *
 * The server builds this name and has already stripped it to letters, digits
 * and hyphens, so a crafted category name cannot steer it (SEC-4). Trusting
 * it here is safe for the same reason — but the fallback matters, because a
 * download named `undefined` is one a partner cannot find again.
 */
export function filenameFrom(contentDisposition: string | undefined, fallback: string): string {
  const matched = contentDisposition?.match(FILENAME)?.[1];
  return matched?.trim() ? matched.trim() : fallback;
}

/**
 * A Blob as text, via FileReader rather than `Blob.text()`.
 *
 * `Blob.text()` would be shorter, but it is missing on iOS Safari before 14 —
 * and this is a PWA that partners install on whatever phone they have (NFR-3).
 * FileReader has been everywhere for a decade. It is also what jsdom
 * implements, so the error path below is testable rather than taken on trust.
 */
const blobAsText = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the response'));
    reader.readAsText(blob);
  });

/**
 * An error whose body arrived as a Blob, turned back into the JSON envelope.
 *
 * With `responseType: 'blob'` axios hands back the *error* body as a Blob too,
 * so `errorMessageKey` would see an object with no `error.code` and report a
 * network failure for what was really a 422. Rewriting `response.data` in
 * place means every caller keeps using the same `apiError` helpers.
 */
async function withReadableErrorBody(error: unknown): Promise<unknown> {
  const response = (error as { response?: { data?: unknown } } | undefined)?.response;
  if (!(response?.data instanceof Blob)) return error;

  try {
    response.data = JSON.parse(await blobAsText(response.data));
  } catch {
    // A genuinely non-JSON body (a proxy's HTML error page, say). Leaving it
    // alone lets errorMessageKey fall through to its generic case rather than
    // throwing over a failure that is already a failure.
  }

  return error;
}

/**
 * Downloads `path` and saves it, returning the filename used.
 *
 * Throws the axios error — with a readable body — so the calling screen can
 * show it. A download that fails must say so: the previous behaviour was a tap
 * that did nothing at all, which reads as a broken button rather than as a
 * problem worth reporting.
 */
export async function downloadFile(
  path: string,
  params: Record<string, string>,
  fallbackName: string,
): Promise<string> {
  let response;
  try {
    response = await api.get<Blob>(path, { params, responseType: 'blob' });
  } catch (error) {
    throw await withReadableErrorBody(error);
  }

  const filename = filenameFrom(response.headers['content-disposition'] as string | undefined, fallbackName);

  const url = URL.createObjectURL(response.data);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    // Appended before clicking: Firefox ignores a click on a detached node.
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Always revoked, even if the click threw. Each object URL pins its blob
    // in memory until it is released, and a partner exporting a month of
    // reports would otherwise hold every one of them for the whole session.
    URL.revokeObjectURL(url);
  }

  return filename;
}
